import * as THREE from "three";
import { EnergyLine } from "./EnergyLine.js";

export class InputHandler {
  constructor(camera, domElement, grid, gameLogic) {
    this.camera = camera;
    this.domElement = domElement;
    this.grid = grid;
    this.gameLogic = gameLogic;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.isDrawing = false;
    this.startCrystal = null;
    this.currentPath = [];
    this.linePoints = [];
    this.energyLine = null;
    this.highlightSquare = this.createHighlightSquare();
    this.debugMode = true;
    this.validMoveHighlights = [];
    this.enabled = false;
    this.grid.scene.add(this.highlightSquare);

    // Store reference to orbit controls and sound manager
    this.controls = null;
    this.soundManager = null;

    // Touch event handling
    this.isTouching = false;
    this.touchStartTime = 0;
    this.lastTouchPosition = { x: 0, y: 0 };

    this.setupEventListeners();
  }

  setSoundManager(soundManager) {
    this.soundManager = soundManager;
  }

  setupEventListeners() {
    // Mouse events
    this.domElement.addEventListener("mousedown", (e) => {
      if (e.button === 0) {
        // Left mouse button
        e.preventDefault();
        this.onPointerDown(e);
      }
    });

    this.domElement.addEventListener("mousemove", (e) => {
      if (this.isDrawing) {
        e.preventDefault();
        this.onPointerMove(e);
      }
    });

    this.domElement.addEventListener("mouseup", (e) => {
      if (e.button === 0) {
        // Left mouse button
        e.preventDefault();
        this.onPointerUp();
      }
    });

    // Touch events with passive: false for better mobile handling
    this.domElement.addEventListener(
      "touchstart",
      (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        this.isTouching = true;
        this.touchStartTime = Date.now();
        this.lastTouchPosition.x = touch.clientX;
        this.lastTouchPosition.y = touch.clientY;
        this.onPointerDown(touch);
      },
      { passive: false }
    );

    this.domElement.addEventListener(
      "touchmove",
      (e) => {
        e.preventDefault();
        if (this.isTouching) {
          const touch = e.touches[0];
          const deltaX = touch.clientX - this.lastTouchPosition.x;
          const deltaY = touch.clientY - this.lastTouchPosition.y;
          this.lastTouchPosition.x = touch.clientX;
          this.lastTouchPosition.y = touch.clientY;
          this.onPointerMove(touch);
        }
      },
      { passive: false }
    );

    this.domElement.addEventListener(
      "touchend",
      (e) => {
        e.preventDefault();
        this.isTouching = false;
        this.onPointerUp();
      },
      { passive: false }
    );
  }

  updateMousePosition(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  getSelectableCrystals() {
    const crystals = [];
    this.grid.crystals.forEach((row) => {
      row.forEach((crystal) => {
        if (crystal && crystal.mesh) {
          // Add all children that have isCrystal flag (both crystal mesh and hit area)
          crystal.mesh.children.forEach((child) => {
            if (child.isCrystal) {
              crystals.push(child);
            }
          });
        }
      });
    });
    return crystals;
  }

  findCrystalInGrid(object) {
    if (object.isCrystal) {
      for (let row = 0; row < this.grid.gridSize; row++) {
        for (let col = 0; col < this.grid.gridSize; col++) {
          const crystal = this.grid.crystals[row][col];
          if (
            crystal &&
            (crystal.crystalMesh === object ||
              crystal.mesh === object.crystalContainer)
          ) {
            return { crystal, row, col };
          }
        }
      }
    }
    return null;
  }

  setOrbitControls(controls) {
    this.controls = controls;
  }

  setGame(game) {
    this.game = game;
  }

  setSoundManager(soundManager) {
    this.soundManager = soundManager;
  }

  getIntersectionPosition(event) {
    this.updateMousePosition(event);
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Check intersection with the grid
    const gridIntersects = this.raycaster.intersectObject(this.grid.gridMesh);
    if (gridIntersects.length > 0) {
      const point = gridIntersects[0].point;
      const offset = ((this.grid.gridSize - 1) * this.grid.spacing) / 2;
      const col = Math.round((point.x + offset) / this.grid.spacing);
      const row = Math.round((point.z + offset) / this.grid.spacing);

      // Check if position is within grid bounds
      if (
        row >= 0 &&
        row < this.grid.gridSize &&
        col >= 0 &&
        col < this.grid.gridSize
      ) {
        return { row, col };
      }
    }
    return null;
  }

  onPointerDown(event) {
    if (!this.enabled) return;

    // Get the clicked position
    const position = this.getIntersectionPosition(event);
    if (!position) return;

    // Get crystal at clicked position
    const crystalInfo = this.grid.getCrystalAtPosition(
      position.row,
      position.col
    );
    if (!crystalInfo) return;

    // Reset the current path
    this.currentPath = [];
    this.linePoints = [];

    // Reset sound manager's path length counter
    if (this.soundManager) {
      this.soundManager.resetPathLength();
    }

    // Add the first crystal to the path
    this.currentPath.push(crystalInfo.crystal);
    this.linePoints.push(crystalInfo.position);

    this.updateMousePosition(event);
    this.raycaster.setFromCamera(this.mouse, this.camera);

    const intersects = this.raycaster.intersectObjects(
      this.getSelectableCrystals(),
      true
    );

    if (intersects.length > 0) {
      const crystalInfo = this.findCrystalInGrid(intersects[0].object);
      if (crystalInfo && crystalInfo.crystal.type === "positive") {
        // Play tap sound
        if (this.soundManager) {
          this.soundManager.playTap();
        }

        // Disable orbit controls when starting a connection
        if (this.controls) {
          this.controls.enabled = false;
        }

        this.isDrawing = true;
        this.startCrystal = crystalInfo.crystal;
        this.currentPath = [crystalInfo.crystal];

        // Add initial point and cursor point
        const point = new THREE.Vector3(
          crystalInfo.crystal.mesh.position.x,
          0.5,
          crystalInfo.crystal.mesh.position.z
        );
        this.linePoints = [point, point.clone()];

        // Create initial line
        this.createLine();

        // Show valid moves from this crystal
        this.showValidMoves(crystalInfo.crystal);
      }
    }
  }

  onPointerMove(event) {
    if (!this.isDrawing || !this.startCrystal) return;

    this.updateMousePosition(event);
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Check grid intersection first
    const gridIntersects = this.raycaster.intersectObject(this.grid.gridMesh);
    if (gridIntersects.length > 0) {
      const point = gridIntersects[0].point;
      const offset = ((this.grid.gridSize - 1) * this.grid.spacing) / 2;

      // Instead of rounding to grid cells, get the exact position
      const exactCol = (point.x + offset) / this.grid.spacing;
      const exactRow = (point.z + offset) / this.grid.spacing;

      // Round to get the nearest grid cell
      const col = Math.round(exactCol);
      const row = Math.round(exactRow);

      // Get the last crystal in our path
      const lastCrystal = this.currentPath[this.currentPath.length - 1];
      const previousCrystal =
        this.currentPath.length > 1
          ? this.currentPath[this.currentPath.length - 2]
          : null;

      // Get the last point position
      let lastPos = null;
      if (this.linePoints.length > 1) {
        const lastPoint = this.linePoints[this.linePoints.length - 2];
        lastPos = {
          row: Math.round((lastPoint.z + offset) / this.grid.spacing),
          col: Math.round((lastPoint.x + offset) / this.grid.spacing),
        };
      }

      // Check if position is within grid bounds and adjacent to last position
      if (
        row >= 0 &&
        row < this.grid.gridSize &&
        col >= 0 &&
        col < this.grid.gridSize &&
        lastPos
      ) {
        // Check if the new position is adjacent to the last position
        const rowDiff = Math.abs(lastPos.row - row);
        const colDiff = Math.abs(lastPos.col - col);
        const isAdjacent = rowDiff <= 1 && colDiff <= 1;

        // Check if target position is valid
        const targetCrystal = this.grid.crystals[row][col];

        // Only skip if this is a crystal and it's the previous crystal
        if (targetCrystal && targetCrystal === previousCrystal) {
          this.highlightSquare.visible = false;
          return;
        }

        // Check if the target is valid (either empty or a valid crystal)
        let isValidTarget = false;
        if (!targetCrystal) {
          isValidTarget = this.gameLogic.validateEmptyPosition(
            this.currentPath,
            row,
            col
          );
        } else {
          isValidTarget = this.gameLogic.validateConnection(
            lastCrystal,
            targetCrystal,
            this.currentPath
          );
        }

        // Calculate distance from exact position to center of grid cell
        const distanceToCenter = Math.sqrt(
          Math.pow(exactCol - col, 2) + Math.pow(exactRow - row, 2)
        );

        // Define a threshold for how close to the center we need to be
        // Lower values make it harder to connect (must be closer to center)
        // Higher values make it easier (can be further from center)
        const proximityThreshold = targetCrystal ? 0.3 : 0.5;

        // Only proceed if we're close enough to the center or it's an empty cell
        const isCloseEnough = distanceToCenter <= proximityThreshold;

        if (isAdjacent && isValidTarget && (isCloseEnough || !targetCrystal)) {
          const x = col * this.grid.spacing - offset;
          const z = row * this.grid.spacing - offset;
          this.highlightSquare.position.set(x, 0.5, z);
          this.highlightSquare.visible = true;

          // Update the cursor point
          const cursorPoint = new THREE.Vector3(x, 0.5, z);

          // Only update if the point has changed
          if (
            !this.linePoints[this.linePoints.length - 1].equals(cursorPoint)
          ) {
            this.linePoints[this.linePoints.length - 1] = cursorPoint;

            // Create or update the energy line
            if (!this.energyLine && this.linePoints.length >= 2) {
              this.createLine();
            } else if (this.energyLine) {
              this.energyLine.generatePath(this.linePoints);
            }
          }

          // If it's an empty square or a new valid crystal
          if (!targetCrystal || !this.currentPath.includes(targetCrystal)) {
            // Add point to the line if it's a new position
            if (
              !this.linePoints.slice(0, -1).find((p) => p.equals(cursorPoint))
            ) {
              this.linePoints[this.linePoints.length - 1] = cursorPoint.clone();
              this.linePoints.push(cursorPoint.clone());

              // Update the energy line
              if (!this.energyLine && this.linePoints.length >= 2) {
                this.createLine();
              } else if (this.energyLine) {
                this.energyLine.generatePath(this.linePoints);
              }

              // If it's a crystal, add it to the path
              if (targetCrystal) {
                this.currentPath.push(targetCrystal);
                // Play connection sound
                if (this.soundManager) {
                  this.soundManager.playConnection();
                }
                // Show temporary highlight effect for neutral crystals
                if (targetCrystal.type === "neutral") {
                  const material = targetCrystal.crystalMesh.material;
                  material.emissive.setHex(this.startCrystal.color);
                  material.emissiveIntensity = 0.3;
                }
              }

              // Show valid moves from the new position
              if (targetCrystal) {
                this.showValidMoves(targetCrystal);
              } else {
                this.showValidMoves(null);
              }
            }
          }
        } else {
          // We're not close enough to the center, so just update the line endpoint
          // without adding a new point or connecting to the crystal
          const exactX = exactCol * this.grid.spacing - offset;
          const exactZ = exactRow * this.grid.spacing - offset;
          const cursorPoint = new THREE.Vector3(exactX, 0.5, exactZ);

          // Update the last point in the line to follow the cursor
          if (this.linePoints.length > 1) {
            this.linePoints[this.linePoints.length - 1] = cursorPoint;
            if (this.energyLine) {
              this.energyLine.generatePath(this.linePoints);
            }
          }

          this.highlightSquare.visible = false;
        }
      }
    }
  }

  onPointerUp() {
    if (!this.enabled) return;

    // Re-enable orbit controls only if they were enabled before
    if (this.controls && this.game && this.game.orbitControlsEnabled) {
      this.controls.enabled = true;
    }

    this.isDrawing = false;
    this.highlightSquare.visible = false;
    this.clearValidMoveHighlights();

    // Reset any temporary highlight effects
    this.currentPath.forEach((crystal) => {
      if (crystal.type === "neutral") {
        const material = crystal.crystalMesh.material;
        material.emissive.setHex(0xffffff);
        material.emissiveIntensity = 0.2;
      }
    });

    // Process the connection if we have a valid path
    if (this.currentPath.length >= 2) {
      const startCrystal = this.currentPath[0];
      const endCrystal = this.currentPath[this.currentPath.length - 1];
      const isValid = this.gameLogic.processConnection(
        startCrystal,
        endCrystal,
        this.currentPath
      );

      // Play connected sound if the connection was valid
      if (isValid && this.soundManager) {
        this.soundManager.playConnected();
      }
    }

    // Clear the current line and path
    this.clearLine();
    this.currentPath = [];
    this.startCrystal = null;
  }

  update(deltaTime) {
    // Update energy line animation
    if (this.energyLine) {
      // Use elapsed time for shader animation
      const time = performance.now() * 0.001; // Convert to seconds
      this.energyLine.update(time);
    }
  }

  createLine() {
    // Clean up existing line
    if (this.energyLine) {
      if (this.energyLine.mesh) {
        this.grid.scene.remove(this.energyLine.mesh);
      }
      this.energyLine.dispose();
      this.energyLine = null;
    }

    if (this.linePoints.length < 2) return;

    // Create new energy line
    this.energyLine = new EnergyLine({
      color: new THREE.Color(this.currentPath[0].color),
      intensity: 1.0 + this.currentPath.length * 0.2,
      radius0: 0.1,
      radius1: 0.1,
      maxIterations: 20,
      straightness: 0.8,
    });

    // Generate path through all points
    const mesh = this.energyLine.generatePath(this.linePoints);
    if (mesh) {
      this.grid.scene.add(mesh);
    }
  }

  updateLine() {
    if (!this.energyLine || !this.linePoints || this.linePoints.length < 2)
      return;

    // Update path through all points
    this.energyLine.generatePath(this.linePoints);
    this.energyLine.setIntensity(1.0 + this.currentPath.length * 0.2);
  }

  clearLine() {
    if (this.energyLine) {
      if (this.energyLine.mesh) {
        this.grid.scene.remove(this.energyLine.mesh);
      }
      this.energyLine.dispose();
      this.energyLine = null;
    }
    this.linePoints = [];
  }

  handleEmptyPositionClick(row, col) {
    // Validate if we can connect to this empty position
    if (this.gameLogic.validateEmptyPosition(this.currentPath, row, col)) {
      // Add visual feedback for the empty position (optional)
      this.updateLine();
    }
  }

  createHighlightSquare() {
    // Create a square outline using edges
    const size = this.grid.spacing * 0.9;
    const geometry = new THREE.PlaneGeometry(size, size);
    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.4,
      linewidth: 2,
    });
    const square = new THREE.LineSegments(edges, material);
    square.rotation.x = -Math.PI / 2;
    square.position.y = 0.5; // Changed from 0.01 to 0.5 to match hitbox layer
    square.visible = false;
    return square;
  }

  createDebugHighlight() {
    const size = this.grid.spacing * 0.9;
    const geometry = new THREE.PlaneGeometry(size, size);
    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.6,
      linewidth: 3,
    });
    const square = new THREE.LineSegments(edges, material);
    square.rotation.x = -Math.PI / 2;
    square.position.y = 0.5; // Changed from 0.01 to 0.5 to match hitbox layer
    return square;
  }

  showValidMoves(crystal) {
    // Clear existing highlights
    this.clearValidMoveHighlights();

    if (!this.debugMode) return;

    // Find position of the current point (crystal or empty slot)
    let currentPos = null;
    if (crystal) {
      // If we have a crystal, find its position
      for (let row = 0; row < this.grid.gridSize; row++) {
        for (let col = 0; col < this.grid.gridSize; col++) {
          if (this.grid.crystals[row][col] === crystal) {
            currentPos = { row, col };
            console.log(`Current crystal at [${row}, ${col}]:`, {
              type: crystal.type,
              color: crystal.color,
              path: this.currentPath.map((c) => ({
                type: c.type,
                color: c.color,
              })),
            });
            break;
          }
        }
        if (currentPos) break;
      }
    } else if (this.linePoints.length > 0) {
      // If we're at an empty position, use the last line point
      const lastPoint = this.linePoints[this.linePoints.length - 1];
      const offset = ((this.grid.gridSize - 1) * this.grid.spacing) / 2;
      currentPos = {
        row: Math.round((lastPoint.z + offset) / this.grid.spacing),
        col: Math.round((lastPoint.x + offset) / this.grid.spacing),
      };
      console.log("Current empty position:", currentPos);
    }

    if (!currentPos) return;

    // Get the previous crystal in the path (if any)
    const previousCrystal =
      this.currentPath.length > 1
        ? this.currentPath[this.currentPath.length - 2]
        : null;

    console.log("Checking adjacent positions from:", currentPos);
    console.log(
      "Current path:",
      this.currentPath.map((c) => ({ type: c.type, color: c.color }))
    );

    // Check all adjacent positions (including diagonals)
    for (let rowOffset = -1; rowOffset <= 1; rowOffset++) {
      for (let colOffset = -1; colOffset <= 1; colOffset++) {
        if (rowOffset === 0 && colOffset === 0) continue; // Skip current position

        const newRow = currentPos.row + rowOffset;
        const newCol = currentPos.col + colOffset;

        // Check if position is within grid bounds
        if (
          newRow >= 0 &&
          newRow < this.grid.gridSize &&
          newCol >= 0 &&
          newCol < this.grid.gridSize
        ) {
          const targetCrystal = this.grid.crystals[newRow][newCol];
          const position = `[${newRow}, ${newCol}]`;

          // Only skip if this is a crystal and it's the previous crystal
          if (targetCrystal && targetCrystal === previousCrystal) {
            console.log(`${position}: Skipped (previous crystal)`);
            continue;
          }

          // Empty positions are always valid targets when we have a path started
          let isValidTarget = false;
          if (!targetCrystal) {
            // Empty positions are always valid if we have a path started
            isValidTarget = this.gameLogic.validateEmptyPosition(
              this.currentPath,
              newRow,
              newCol
            );
            console.log(`${position}: Empty slot, valid:`, isValidTarget, {
              pathLength: this.currentPath.length,
              firstCrystal: this.currentPath[0]
                ? {
                    type: this.currentPath[0].type,
                    color: this.currentPath[0].color,
                  }
                : null,
            });
          } else {
            // For crystals, validate against the current path
            isValidTarget = this.gameLogic.validateConnection(
              crystal || this.currentPath[this.currentPath.length - 1],
              targetCrystal,
              this.currentPath
            );
            console.log(`${position}: Crystal`, {
              type: targetCrystal.type,
              color: targetCrystal.color,
              valid: isValidTarget,
            });
          }

          if (isValidTarget) {
            // Create and position highlight
            const highlight = this.createDebugHighlight();
            const offset = ((this.grid.gridSize - 1) * this.grid.spacing) / 2;
            const x = newCol * this.grid.spacing - offset;
            const z = newRow * this.grid.spacing - offset;
            highlight.position.set(x, 0.5, z);

            this.grid.scene.add(highlight);
            this.validMoveHighlights.push(highlight);
          }
        }
      }
    }
  }

  clearValidMoveHighlights() {
    this.validMoveHighlights.forEach((highlight) => {
      this.grid.scene.remove(highlight);
    });
    this.validMoveHighlights = [];
  }

  createSelectedHighlight(color) {
    const size = this.grid.spacing * 0.9;
    const geometry = new THREE.PlaneGeometry(size, size);
    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.8,
      linewidth: 6,
    });
    const square = new THREE.LineSegments(edges, material);
    square.rotation.x = -Math.PI / 2;
    square.position.y = 0.01; // Slightly above the grid
    return square;
  }
}
